import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import {
  ExtendedGfhAccount,
  TExtendedGfhAccountsPaginated,
} from "@core/models/extended-gfh-account.model";
import { PendingNav } from "@core/models/pending-nav.model";
import { ProductVersions } from "@core/models/product-versions.model";
import { DashboardService } from "@core/services/dashboard.service";
import { NotificationService } from "@core/services/notification.service";
import { TableSorting, TPagination } from "@custom-types/sorting";
import { ToasterSeverities } from "@enums/toaster-severities";
import * as dashboardActions from "@features/dashboard/store/dashboard.actions";
import {
  SET_APPROVAL_TABLE_LOADING,
  SET_DASHBOARD_LOADING,
  SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING,
  SET_SELF_APPROVAL_TABLE_LOADING,
} from "@features/dashboard/store/dashboard.actions";
import { PendingIban } from "@models/pending-iban.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import { SELECT_PRODUCT_VERSIONS_PAGINATION } from "./dashboard.selector";

@Injectable()
export class DashboardEffects {
  loadPendingNavList$ = createEffect((): Observable<Action> => {
    // TODO delete later @Adrian
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_PENDING_NAV),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_DASHBOARD_LOADING({ loading: true }));
        return this.dashboardService.getPendingNav$().pipe(
          switchMap((pendingNavList: Array<PendingNav>): Observable<Action> => {
            this.store.dispatch(SET_DASHBOARD_LOADING({ loading: false }));
            return of(
              dashboardActions.SET_PENDING_NAV({
                pendingNavList: pendingNavList,
              })
            );
          }),
          catchError((error: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(SET_DASHBOARD_LOADING({ loading: false }));
            return of(dashboardActions.DASHBOARD_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  loadProductVersionList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_PRODUCT_VERSION_CHANGED),
      withLatestFrom(this.store.select(SELECT_PRODUCT_VERSIONS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_DASHBOARD_LOADING({ loading: true }));
          return this.dashboardService
            .getProductVersionChanged$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response:
                    | Array<ExtendedGfhAccount>
                    | TExtendedGfhAccountsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_DASHBOARD_LOADING({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      dashboardActions.SET_PRODUCT_VERSION_CHANGED_LIST({
                        productVersionList: data,
                      }),
                      dashboardActions.SET_PRODUCT_VERSION_CHANGED_LENGTH({
                        length: response.length,
                      }),
                      dashboardActions.SET_PRODUCT_VERSION_CHANGED_PAGINATION({
                        pagination,
                      })
                    );
                  }
                  return of(
                    dashboardActions.SET_PRODUCT_VERSION_CHANGED_LIST({
                      productVersionList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  dashboardActions.SET_PRODUCT_VERSION_CHANGED_LIST({
                    productVersionList: [],
                  }),
                  dashboardActions.SET_PRODUCT_VERSION_CHANGED_LENGTH({
                    length: 0,
                  }),
                  dashboardActions.SET_PRODUCT_VERSION_CHANGED_PAGINATION({
                    pagination: undefined,
                  }),
                  dashboardActions.SET_DASHBOARD_LOADING({ loading: false })
                );
              })
            );
        }
      )
    );
  });

  loadSelfApprovalList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_SELF_APPROVAL_TABLE),
      mergeMap(
        (action: {
          canApprove: boolean;
          sorting?: TableSorting;
        }): Observable<Action> => {
          this.store.dispatch(
            SET_SELF_APPROVAL_TABLE_LOADING({ selfApprovalLoading: true })
          );
          return this.dashboardService
            .getApprovalTaskList$(action.canApprove, action.sorting)
            .pipe(
              switchMap((list: Array<PendingIban>): Observable<Action> => {
                this.store.dispatch(
                  SET_SELF_APPROVAL_TABLE_LOADING({
                    selfApprovalLoading: false,
                  })
                );
                return of(
                  dashboardActions.SET_SELF_APPROVAL_TABLE({
                    selfApprovalList: list,
                  })
                );
              }),
              catchError((): Observable<Action> => {
                return of(
                  dashboardActions.SET_SELF_APPROVAL_TABLE_LOADING({
                    selfApprovalLoading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadApprovalList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_APPROVAL_TABLE),
      mergeMap(
        (action: {
          canApprove: boolean;
          sorting?: TableSorting;
        }): Observable<Action> => {
          this.store.dispatch(
            SET_APPROVAL_TABLE_LOADING({ approvalLoading: true })
          );
          return this.dashboardService
            .getApprovalTaskList$(action.canApprove, action.sorting)
            .pipe(
              switchMap((list: Array<PendingIban>): Observable<Action> => {
                this.store.dispatch(
                  SET_APPROVAL_TABLE_LOADING({ approvalLoading: false })
                );
                return of(
                  dashboardActions.SET_APPROVAL_TABLE({ approvalList: list })
                );
              }),
              catchError((): Observable<Action> => {
                return of(
                  dashboardActions.SET_APPROVAL_TABLE_LOADING({
                    approvalLoading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadLastSevenDaysApprovedTable$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_LAST_SEVEN_DAYS_APPROVED_TABLE),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
            lastSevenDaysLoading: true,
          })
        );
        return this.dashboardService
          .getLastSevenDaysCompletedTaskList$(action.sorting)
          .pipe(
            switchMap((list: Array<PendingIban>): Observable<Action> => {
              this.store.dispatch(
                SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
                  lastSevenDaysLoading: false,
                })
              );
              return of(
                dashboardActions.SET_LAST_SEVEN_DAYS_APPROVED_TABLE({
                  lastSevenDaysList: list,
                })
              );
            }),
            catchError((): Observable<Action> => {
              return of(
                dashboardActions.SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
                  lastSevenDaysLoading: false,
                })
              );
            })
          );
      })
    );
  });

  saveAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.APPROVE_OR_DECLINE_TASK),
      mergeMap(
        (action: {
          path: number;
          id: string;
          decision: boolean;
        }): Observable<Action> => {
          this.store.dispatch(
            SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
              lastSevenDaysLoading: true,
            })
          );
          this.store.dispatch(
            SET_SELF_APPROVAL_TABLE_LOADING({ selfApprovalLoading: true })
          );
          return this.dashboardService
            .approveOrDeclineTask$(action.path, action.id, action.decision)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
                    lastSevenDaysLoading: false,
                  })
                );
                this.store.dispatch(
                  SET_SELF_APPROVAL_TABLE_LOADING({
                    selfApprovalLoading: false,
                  })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(
                  dashboardActions.LOAD_SELF_APPROVAL_TABLE({
                    canApprove: true,
                  }),
                  dashboardActions.LOAD_LAST_SEVEN_DAYS_APPROVED_TABLE({})
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return of(
                  dashboardActions.SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING({
                    lastSevenDaysLoading: false,
                  }),
                  dashboardActions.SET_SELF_APPROVAL_TABLE_LOADING({
                    selfApprovalLoading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadProductVersions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.LOAD_PRODUCT_VERSIONS),
      mergeMap((action: { id: string }): Observable<Action> => {
        this.store.dispatch(SET_DASHBOARD_LOADING({ loading: true }));
        return this.dashboardService.getProductVersions$(action.id).pipe(
          switchMap((productVersions: ProductVersions): Observable<Action> => {
            return of(
              dashboardActions.SET_DASHBOARD_LOADING({ loading: false }),
              dashboardActions.SET_PRODUCT_VERSIONS({ productVersions })
            );
          }),
          catchError((): Observable<Action> => {
            return of(
              dashboardActions.SET_DASHBOARD_LOADING({ loading: false })
            );
          })
        );
      })
    );
  });

  saveAccountCostRules$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dashboardActions.SAVE_ACCOUNT_COST_RULES),
      mergeMap(
        (action: {
          gfhAccountInformation: Partial<ExtendedGfhAccount>;
        }): Observable<Action> => {
          this.store.dispatch(SET_DASHBOARD_LOADING({ loading: true }));
          return this.dashboardService
            .saveAccountCostRules$(action.gfhAccountInformation)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(dashboardActions.LOAD_PRODUCT_VERSION_CHANGED({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return of(
                  dashboardActions.SET_DASHBOARD_LOADING({ loading: false })
                );
              })
            );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private dashboardService: DashboardService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
